import axios, { CancelToken } from 'axios';

import { Channel, FilterGroup, dropEmptyFields } from '../models/ChannelModel';
import {
  ChannelStageCounts,
  FunnelTotals,
  StageCounts,
} from '../models/FunnelModel';

const API_URL = process.env.REACT_APP_API_URL;

// Safety net above the server's fixed statement timeout (~20s): a correct
// response can never take longer than that, so this only aborts truly hung
// connections (surfacing as ECONNABORTED) instead of spinning forever.
const FUNNEL_REQUEST_TIMEOUT_MS = 30000;

export type AcquisitionFunnelParams = {
  since: string;
  until: string;
  store_id?: number | null;
  groups: FilterGroup[];
  page?: number;
  page_size?: number;
};

// Respuestas recientes por parámetros exactos: volver a un rango, tienda,
// filtro o página ya consultados no repite la consulta (son pesadas). Solo se
// guardan las que salen bien; vive en memoria hasta recargar la página.
const FUNNEL_CACHE_TTL_MS = 5 * 60 * 1000;
const funnelCache = new Map<
  string,
  { expiresAt: number; data: FunnelTotals }
>();

export function fetchAcquisitionFunnel(
  params: AcquisitionFunnelParams,
  cancelToken?: CancelToken
): Promise<FunnelTotals> {
  const key = JSON.stringify(params);
  const cached = funnelCache.get(key);
  if (cached && cached.expiresAt > Date.now()) {
    return Promise.resolve(cached.data);
  }

  const endpoint = `${API_URL}/api/bi/acquisition-funnel/`;
  return axios
    .post<FunnelTotals>(endpoint, params, {
      cancelToken,
      timeout: FUNNEL_REQUEST_TIMEOUT_MS,
    })
    .then((response) => {
      const now = Date.now();
      funnelCache.forEach((entry, entryKey) => {
        if (entry.expiresAt <= now) funnelCache.delete(entryKey);
      });
      funnelCache.set(key, {
        expiresAt: now + FUNNEL_CACHE_TTL_MS,
        data: response.data,
      });
      return response.data;
    });
}

// El listado de sesiones no hace falta para contar: se pide la página mínima.
const COUNTS_ONLY_PAGE_SIZE = 1;

export type ChannelMixParams = Pick<
  AcquisitionFunnelParams,
  'since' | 'until' | 'store_id'
>;

async function fetchFunnelCounts(
  params: ChannelMixParams & { groups: FilterGroup[] },
  cancelToken: CancelToken
): Promise<StageCounts> {
  const { sessions, leads, trials, subscriptions } =
    await fetchAcquisitionFunnel(
      { ...params, page_size: COUNTS_ONLY_PAGE_SIZE },
      cancelToken
    );
  return { sessions, leads, trials, subscriptions };
}

// Una petición al embudo por canal, de una en una: en paralelo saturan el
// servidor y dejan colgada la consulta del propio embudo. Cada canal se entrega
// a onChannel en cuanto llega; uno que falla (p.ej. timeout) llega con
// counts=null sin cortar al resto. Cancelar sí rechaza la promesa.
export async function fetchFunnelByChannel(
  params: ChannelMixParams,
  channels: Channel[],
  cancelToken: CancelToken,
  onChannel: (channel: ChannelStageCounts) => void
): Promise<void> {
  for (const { name, groups } of channels) {
    try {
      const counts = await fetchFunnelCounts(
        { ...params, groups: groups.map(dropEmptyFields) },
        cancelToken
      );
      onChannel({ name, counts });
    } catch (err) {
      if (axios.isCancel(err)) throw err;
      onChannel({ name, counts: null });
    }
  }
}
